import { FreeGiftPromotionContextValue } from '@/components/blocks/FreeGiftV2/context/FreeGiftPromotionContext';
import {
	buildMarketInventoryIndex,
	filterValidCombinablePromotions,
	getCombinablePromotions,
	getPurchasesQuantities,
	getTargetQuantity,
} from '@/components/blocks/FreeGiftV2/utils';
import { usePhysicalStoreData } from '@/data/Content/usePhysicalStoreData';
import { useStoreIDS } from '@/data/state/useStoreIDs';
import { ProductType } from '@/data/types/Product';
import { CombinablePromotion } from '@/data/types/Promotions';
import { useMemo, useState } from 'react';

interface UseFreeGiftPromotionCalculationsParams {
	combinablePromotions: CombinablePromotion[];
	currentPartNumber: string;
	promoItems: ProductType[];
	isRewardView: boolean;
}

export const useFreeGiftPromotionCalculations = ({
	combinablePromotions,
	currentPartNumber,
	promoItems,
	isRewardView,
}: UseFreeGiftPromotionCalculationsParams): FreeGiftPromotionContextValue => {
	const { physicalStoreId } = useStoreIDS();
	const { marketId } = usePhysicalStoreData({ physicalStoreId });

	const promotions = useMemo(
		() => filterValidCombinablePromotions(combinablePromotions),
		[combinablePromotions]
	);

	const marketInventory = useMemo(
		() => buildMarketInventoryIndex(promoItems, marketId),
		[promoItems, marketId]
	);

	const promotionGroups = useMemo(
		() => getCombinablePromotions(promotions, currentPartNumber, isRewardView),
		[promotions, currentPartNumber, isRewardView]
	);

	const [selectedPromotionIndex, setSelectedPromotionIndex] = useState(
		promotionGroups.length > 1 ? -1 : 0
	);

	return useMemo(() => {
		const { purchase, reward } = promotionGroups[selectedPromotionIndex] ?? {
			reward: {
				combinable: false,
				rewards: [{ targetGroupId: currentPartNumber, quantity: 1, targetSKUs: [] }],
			},
			purchase: {
				combinable: false,
				purchases: [
					{
						triggerGroupId: currentPartNumber,
						quantity: 1,
						triggerSKUs: [{ sku: currentPartNumber, quantity: 1 }],
					},
				],
			},
		};
		const promoThdCode = promotionGroups[selectedPromotionIndex]?.promoThdCode;
		const isPurchaseCombinable = purchase.combinable;
		const { rewardsQty, showUpToLabel } = getTargetQuantity(reward);

		const { othersQty, parentQty: purchaseQty } = getPurchasesQuantities(
			purchase,
			currentPartNumber
		);

		return {
			currentPartNumber,
			combinablePromotions,
			purchase,
			reward,
			promoThdCode,
			isPurchaseCombinable,
			purchaseQty,
			othersQty,
			rewardsQty,
			showUpToLabel,
			promoItems,
			marketInventory,
			promotionGroups,
			selectedPromotionIndex,
			setSelectedPromotionIndex,
		};
	}, [
		promotionGroups,
		selectedPromotionIndex,
		currentPartNumber,
		promoItems,
		combinablePromotions,
		marketInventory,
	]);
};
